import React, { useEffect } from 'react'
import { Trans, t } from '@lingui/macro'
import { useDebouncedCallback } from 'use-debounce'

import {
  useCPDFeatureFlag,
  useFeatureFlags
} from 'front-end/src/app/hooks/useFeatureFlags'
import { notification } from 'front-end/src/components/common/notifications/Notifications'
import { ButtonDanger, Switch, Text } from 'front-end/src/components'
import { Drawer } from '../../../components/common/page/drawer/Drawer'
import DrawerItem from '../../../components/common/page/drawer/DrawerItem'
import Input from '../../../components/common/form/input/Input'
import LinkButton from '../../../components/common/link/LinkButton'
import { PlainWorkspaceType } from '../types'
import { linkBuilder } from '../../../../../shared/linkBuilder'
import { useGroupUpdateMutation } from './SettingsDrawer.codegen'
import { GroupByIdQuery } from '../GroupPage.codegen'
import { GroupSelfSignupLink } from './GroupSelfSignupLink'

import './SettingsDrawer.less'
import { useLooopTheme } from 'front-end/src/public-api/hooks/useLooopTheme'
import { HStack } from '@chakra-ui/react'
import IconBookOpen from 'front-end/src/components/common/icon/IconBookOpen'

interface ISettingsDrawerProps {
  group: NonNullable<NonNullable<GroupByIdQuery['admin']>['group']>
  visible: boolean
  onHide: () => void
  refetchGroup: () => any
  onDelete: () => void
}

const renderWorkspaces = (workspaces: PlainWorkspaceType[]) => (
  <DrawerItem
    className="WorkspacesList full-width"
    label={t`Workspace access:`}
  >
    <ul>
      {workspaces.map((workspace, index) => (
        <li key={index} className="WorkspacesList__item">
          <span className="WorkspacesList__title">{workspace.name}</span>
          {/* TODO: LinkButton does not support target prop. Remove prop or implement logic in component? */}
          <LinkButton
            to={linkBuilder.workspaceMembers(workspace.id)}
            target="_blank"
            className="WorkspacesList__action"
          >
            <Trans>View</Trans>
          </LinkButton>
        </li>
      ))}
    </ul>
  </DrawerItem>
)

export function GroupSettingsDrawer(props: ISettingsDrawerProps) {
  const { cpdAlias, isCPDEnabledInAccountSettings } = useCPDFeatureFlag()
  const { featureFlags } = useFeatureFlags()
  const { theme } = useLooopTheme()
  const { visible, onHide, refetchGroup, onDelete, group } = props
  const { workspaces } = props.group
  const [updateGroup] = useGroupUpdateMutation()

  const debouncedUpdateGroup = useDebouncedCallback(async (params) => {
    await updateGroup(params)
    refetchGroup()
  }, 1000)

  useEffect(() => {
    return debouncedUpdateGroup.flush()
  }, [])

  return (
    <Drawer
      className="SettingsDrawer"
      title={t`Settings`}
      show={visible}
      onHide={onHide}
    >
      <DrawerItem
        className="SettingsDrawerItem__Name full-width"
        label={t`Group name`}
      >
        <Input
          title={t`Group name`}
          light
          defaultValue={group.name}
          onChange={async (val) => {
            await debouncedUpdateGroup({
              variables: {
                id: group.id,
                groupInput: {
                  name: val
                }
              }
            })
          }}
        />
      </DrawerItem>
      {isCPDEnabledInAccountSettings && (
        <>
          <DrawerItem
            label={
              <HStack spacing="16px">
                <IconBookOpen />
                <Trans>Enable {cpdAlias} for this group</Trans>
              </HStack>
            }
            bottom={
              <Text
                textAlign="left"
                fontSize="12px"
                color={theme.colors.grey[400]}
              >
                <Trans>
                  When enabled, learners are able to maintain their {cpdAlias}{' '}
                  activity history and upload evidence of {cpdAlias}.
                </Trans>
              </Text>
            }
          >
            <Switch
              onChange={async () => {
                await updateGroup({
                  variables: {
                    id: group.id,
                    groupInput: {
                      is_cpd: !group.is_cpd
                    }
                  }
                })
                refetchGroup()
              }}
              isChecked={group.is_cpd}
            />
          </DrawerItem>
        </>
      )}
      {workspaces && workspaces.length > 0 && renderWorkspaces(workspaces)}
      {featureFlags?.secret_signup && (
        <DrawerItem>
          <GroupSelfSignupLink />
        </DrawerItem>
      )}

      <DrawerItem
        className="danger"
        label={t`Delete this group`}
        onClick={() => {
          if (group.scimResource) {
            notification.warning(t`SCIM group cannot be removed in Looop.`)
          }
        }}
      >
        <ButtonDanger
          onClick={onDelete}
          isDisabled={!!group.scimResource}
          dataTest="DeleteGroupButton"
        >
          <Trans>Delete</Trans>
        </ButtonDanger>
      </DrawerItem>
    </Drawer>
  )
}
